import type { ToolConfig } from "../types";

export const vehicleTools: ToolConfig[] = [
  // 3. Car Depreciation Calculator
  {
    slug: "car-depreciation-calculator",
    category: "vehicle",
    title: "Car Depreciation Value Calculator",
    shortTitle: "Car Depreciation",
    description: "Calculate vehicle depreciation and resale value over years",
    keywords: ["car depreciation", "vehicle value", "resale value", "depreciation rate"],
    inputs: [
      { type: "number", key: "purchasePrice", label: "Car Purchase Price", prefix: "₹", min: 300000, max: 50000000, step: 100000, default: 1000000 },
      { type: "number", key: "yearsOld", label: "Vehicle Age", suffix: "years", min: 0, max: 20, step: 1, default: 3 },
      { type: "number", key: "depreciationRate", label: "Annual Depreciation Rate", suffix: "%", min: 10, max: 25, step: 1, default: 15 },
    ],
    calculate(v) {
      const price = v.purchasePrice as number;
      const years = v.yearsOld as number;
      const depreciation = (v.depreciationRate as number) / 100;

      const currentValue = Math.round(price * Math.pow(1 - depreciation, years));
      const totalDepreciation = price - currentValue;
      const depreciationPercent = Math.round((totalDepreciation / price) * 100);

      return { purchasePrice: price, currentValue, totalDepreciation, depreciationPercent, yearsOld: years };
    },
    resultFields: [
      { key: "currentValue", label: "Current Car Value", format: "currency", isPrimary: true },
      { key: "purchasePrice", label: "Original Purchase Price", format: "currency" },
      { key: "totalDepreciation", label: "Total Depreciation", format: "currency" },
      { key: "depreciationPercent", label: "Depreciation %", format: "percent" },
    ],
    content: {
      whatIs: "Car depreciation in India typically follows non-linear pattern: 20-30% depreciation in year 1, 15-20% in year 2, and 10-15% annually thereafter, resulting in 50-60% value retention after 5 years. Depreciation factors include: vehicle age, mileage (faster depreciation above 150,000 km), condition, brand reputation, and market demand. Luxury segment depreciates faster (60-70% value loss in 5 years) than mass-market segments (40-50% loss). Fuel type also matters: electric vehicles depreciate slower due to lower operating costs; SUVs depreciate faster due to high initial cost. Understanding depreciation helps calculate vehicle resale value, compare car ownership costs over time, and make informed purchase decisions between new and pre-owned vehicles.",
      faqs: [
        { question: "What's the typical car depreciation rate in India?", answer: "Year 1: 20%, Year 2: 15%, Year 3: 12%, Year 4-5: 10%, After 5: 8% yearly. Varies by brand: Maruti 10-12%, luxury 18-20%." },
        { question: "Why do cars depreciate faster in first few years in India?", answer: "New car loses 'new' status immediately. Technology older after 2-3 years. Parts become readily available (cheaper). Mileage accumulates. Registration transfer costs increase depreciation." },
        { question: "How does mileage affect car depreciation in India?", answer: "Every 1L km: 10-15% value loss. 3L km car: 30-45% depreciation independent of age. Low mileage (5-7k km/year) retains 60-70% value at 5 years." },
        { question: "Which cars depreciate least in India?", answer: "Maruti (10-12% yearly): highest resale value. Hyundai (12-15%): good. Mahindra (15-18%): moderate. Luxury (18-25%): worst. Maintenance history crucial." },
        { question: "Can I reduce car depreciation in India?", answer: "Partial: maintain well (₹50k/year saves 5% depreciation), low mileage (avoid unnecessary driving), keep service records, minor dents/scratches reduced. Can't avoid depreciation entirely." },
      ],
      relatedSlugs: ["vehicle/car-resale-value", "vehicle/car-loan-emi", "vehicle/car-maintenance-cost"],
    },
  },

  // 4. Car Insurance Calculator
  {
    slug: "car-insurance-calculator",
    category: "vehicle",
    title: "Car Insurance Premium Calculator (IDV-Based)",
    shortTitle: "Car Insurance",
    description: "Calculate comprehensive and third-party car insurance premiums",
    keywords: ["car insurance", "IDV", "insurance premium", "vehicle insurance"],
    inputs: [
      { type: "number", key: "idv", label: "Insured Declared Value (IDV)", prefix: "₹", min: 100000, max: 100000000, step: 100000, default: 1000000 },
      { type: "number", key: "ageYears", label: "Vehicle Age", suffix: "years", min: 0, max: 20, step: 1, default: 3 },
      { type: "select", key: "location", label: "Location Type", options: [{ value: "metro", label: "Metro City" }, { value: "tier2", label: "Tier-2 City" }, { value: "rural", label: "Rural" }], default: "metro" },
    ],
    calculate(v) {
      const idv = v.idv as number;
      const age = v.ageYears as number;
      const location = v.location as string;

      const baseRate = 0.035; // 3.5% of IDV
      const locationFactor = location === "metro" ? 1.0 : location === "tier2" ? 0.85 : 0.7;
      const ageFactor = 1 + (age * 0.02); // increases 2% per year

      const annualPremium = Math.round(idv * baseRate * locationFactor * ageFactor);
      const monthlyPremium = Math.round(annualPremium / 12);

      return { annualPremium, monthlyPremium };
    },
    resultFields: [
      { key: "annualPremium", label: "Annual Insurance Premium", format: "currency", isPrimary: true },
      { key: "monthlyPremium", label: "Monthly Premium", format: "currency" },
    ],
    content: {
      whatIs: "Car insurance premium in India is based on IDV (Insured Declared Value) which is the car's current market value. Premium typically: 3.5-5% of IDV annually. Metro cities: 5-7% (higher theft risk), tier-2 cities: 4-5%, rural: 3-4%. No-claim bonus (NCB) provides 20-50% discount for claim-free years. Comprehensive cover includes third-party liability, own damage, theft, natural disasters, vandalism, and personal accident benefit.",
      faqs: [
        { question: "How is IDV calculated for car insurance in India?", answer: "IDV = current market value of car (not purchase price). Use online IDV calculator on insurance websites. For 5-year-old ₹10L car: IDV ₹5-6L typically." },
        { question: "What's the insurance premium range for cars in India?", answer: "3.5-5% of IDV annually. ₹10L car: ₹35k-50k premium. Metro: higher; rural: lower. NCB discount: 20-50% reduces premium significantly." },
        { question: "How much difference does no-claim bonus make in India?", answer: "20% NCB after year 1, 40% after year 2, 50% after year 3+. On ₹40k premium: saves ₹8k year 1, ₹16k year 2, ₹20k year 3. Huge savings for safe drivers." },
        { question: "Is comprehensive car insurance mandatory in India?", answer: "Only third-party liability is mandatory. Comprehensive (damage to own car) optional but highly recommended. Thieves risk justifies comprehensive investment." },
        { question: "Should I increase or decrease IDV for insurance in India?", answer: "Decrease IDV: lower premium (but less claim if accident). Increase IDV: higher premium (but better claim coverage). Balance based on car value and risk." },
      ],
      relatedSlugs: ["insurance/car-insurance-premium", "vehicle/car-loan-emi", "vehicle/road-tax-calculator"],
    },
  },

  // 6. RTO Registration Fee
  {
    slug: "rto-registration-fee",
    category: "vehicle",
    title: "RTO Registration Charges Calculator",
    shortTitle: "RTO Registration Fee",
    description: "Calculate vehicle registration and RC transfer costs by state",
    keywords: ["RTO registration", "registration fee", "transfer fee", "vehicle registration"],
    inputs: [
      { type: "select", key: "registrationType", label: "Registration Type", options: [{ value: "new", label: "New Registration" }, { value: "renewal", label: "Renewal" }, { value: "transfer", label: "Transfer (Change of Owner)" }], default: "new" },
      { type: "number", key: "vehicleAge", label: "Vehicle Age (if renewal/transfer)", suffix: "years", min: 0, max: 20, step: 1, default: 5 },
    ],
    calculate(v) {
      const type = v.registrationType as string;
      const age = v.vehicleAge as number;

      let baseFee = 0;
      let certificateFee = 0;
      let miscFee = 0;

      if (type === "new") {
        baseFee = 1500;
        certificateFee = 500;
        miscFee = 500; // Inspection + documents
      } else if (type === "renewal") {
        baseFee = age > 15 ? 800 : 500;
        certificateFee = 200;
        miscFee = 300;
      } else if (type === "transfer") {
        baseFee = 1000;
        certificateFee = 300;
        miscFee = 500;
      }

      const totalFee = baseFee + certificateFee + miscFee;

      return { baseFee, certificateFee, miscFee, totalFee };
    },
    resultFields: [
      { key: "totalFee", label: "Total RTO Registration Fee", format: "currency", isPrimary: true },
      { key: "baseFee", label: "Base Registration Fee", format: "currency" },
      { key: "certificateFee", label: "Certificate Fee", format: "currency" },
      { key: "miscFee", label: "Miscellaneous Charges", format: "currency" },
    ],
    content: {
      whatIs: "RTO registration fees in India: new registration ₹2,500-4,000, renewal ₹800-1,500 (varies by vehicle age), transfer ₹1,500-2,500. Includes registration certificate, number plate, documentation. Vehicle renewal: every 15 years (old) or ongoing based on state. Transfer: when ownership changes (marriage, sale, inheritance). Process takes 5-15 days at local RTO office.",
      faqs: [
        { question: "What are the total RTO registration costs in India?", answer: "New: ₹2,500-4,000 including road tax. Renewal: ₹800-1,500. Transfer: ₹1,500-2,500. Varies by state and vehicle category." },
        { question: "How long is vehicle registration valid in India?", answer: "Indefinite if vehicle is roadworthy. Fitness certificate renewal: every 15 years for vehicles >5 years. Registration plates changed as needed." },
        { question: "What's the vehicle transfer process in India?", answer: "Old owner: no-objection certificate (NOC) from old RTO. New owner: submits transfer form with documents to new state RTO. Processing: 5-15 days. Can't drive between states during transfer." },
        { question: "Do I need to pay road tax again on transfer in India?", answer: "Yes, new owner pays road tax applicable in their state. Tax transferred to new RTO. Amount depends on new state's tax rate and vehicle value." },
        { question: "Can I renew vehicle registration online in India?", answer: "Most states allow online renewal (Delhi, Maharashtra, Karnataka). Check your RTO website. In-person appointment required for inspection and biometrics." },
      ],
      relatedSlugs: ["vehicle/road-tax-calculator", "vehicle/car-insurance-calculator", "vehicle/car-loan-emi"],
    },
  },

  // 7. Car Resale Value
  {
    slug: "car-resale-value",
    category: "vehicle",
    title: "Car Resale Value Estimator",
    shortTitle: "Car Resale Value",
    description: "Estimate fair resale value for your car based on age and condition",
    keywords: ["resale value", "second-hand car", "used car price", "car valuation"],
    inputs: [
      { type: "number", key: "originalPrice", label: "Original Purchase Price", prefix: "₹", min: 300000, max: 50000000, step: 100000, default: 1000000 },
      { type: "number", key: "ageYears", label: "Vehicle Age", suffix: "years", min: 1, max: 20, step: 1, default: 3 },
      { type: "number", key: "mileageKm", label: "Current Mileage", suffix: "km", min: 10000, max: 5000000, step: 50000, default: 60000 },
      { type: "select", key: "condition", label: "Overall Condition", options: [{ value: "excellent", label: "Excellent" }, { value: "good", label: "Good" }, { value: "fair", label: "Fair" }], default: "good" },
    ],
    calculate(v) {
      const price = v.originalPrice as number;
      const age = v.ageYears as number;
      const mileage = v.mileageKm as number;
      const condition = v.condition as string;

      // Base depreciation: 15% yearly for first 3 years, then 8% yearly
      const depreciationRate = age <= 3 ? 0.15 : 0.08;
      const baseValue = Math.round(price * Math.pow(1 - depreciationRate, age));

      // Mileage adjustment: 10% loss per 100k km
      const mileageAdjustment = 1 - (mileage / 1000000) * 0.10;
      const mileageAdjustedValue = Math.round(baseValue * Math.max(0.3, mileageAdjustment));

      // Condition adjustment
      const conditionMultiplier = condition === "excellent" ? 1.1 : condition === "good" ? 1.0 : 0.85;
      const resaleValue = Math.round(mileageAdjustedValue * conditionMultiplier);

      return { originalPrice: price, resaleValue, valueRetained: Math.round((resaleValue / price) * 100) };
    },
    resultFields: [
      { key: "resaleValue", label: "Estimated Resale Value", format: "currency", isPrimary: true },
      { key: "originalPrice", label: "Original Purchase Price", format: "currency" },
      { key: "valueRetained", label: "Value Retained %", format: "percent" },
    ],
    content: {
      whatIs: "Car resale value depends on: age (15% yearly for first 3 years, 8% thereafter), mileage (10% loss per 100k km), condition (excellent: +10%, fair: -15%), brand (Maruti retains 60-70%; luxury 40-50%), service history, accident record. A ₹10L car: 5 years old, 60k km, good condition = ₹5.5-6L resale value. Resale value lower than depreciation suggests due to buyer negotiation and market conditions.",
      faqs: [
        { question: "What affects car resale value the most in India?", answer: "Mileage (biggest factor), condition, maintenance history, brand, accident history. Each 100k km: 10% loss. Poor maintenance: 20-30% loss." },
        { question: "How do I maximize car resale value in India?", answer: "Regular servicing (full record essential), minimal mileage (5-7k/year optimal), accident-free, good condition, minor repairs timely, clean interior/exterior." },
        { question: "Which cars retain value best in India?", answer: "Maruti (60-70% after 5 years), Hyundai (55-65%), Tata (50-60%). Luxury (40-50%), SUVs (60-70%), sedans (50-60%). Maintenance crucial." },
        { question: "How much should seller ask for used car in India?", answer: "Market rate: 50-60% for 5-year-old cars. Check online portals (CarDekho, OLX, Cashcarsbuyer). Negotiate 10-20% above estimated value, settle at 50-60%." },
        { question: "What documents increase resale value of car in India?", answer: "Full service history, transfer documents, insurance papers, registration, PUC certificate, road tax clearance, no-dues certificate from bank." },
      ],
      relatedSlugs: ["vehicle/car-depreciation-calculator", "vehicle/car-loan-emi", "vehicle/vehicle-scrappage-value"],
    },
  },

  // 8. Fuel Cost Per KM
  {
    slug: "fuel-cost-per-km",
    category: "vehicle",
    title: "Fuel Cost Per Kilometer Calculator",
    shortTitle: "Fuel Cost Per KM",
    description: "Calculate fuel efficiency and fuel cost per kilometer for vehicles",
    keywords: ["fuel cost", "cost per km", "fuel efficiency", "mileage cost"],
    inputs: [
      { type: "number", key: "monthlyKm", label: "Monthly Kilometers", suffix: "km", min: 500, max: 10000, step: 500, default: 2000 },
      { type: "number", key: "fuelEfficiency", label: "Fuel Efficiency", suffix: "km/liter", min: 8, max: 30, step: 1, default: 15 },
      { type: "number", key: "fuelPrice", label: "Fuel Price", prefix: "₹/liter", min: 80, max: 150, step: 5, default: 110 },
    ],
    calculate(v) {
      const km = v.monthlyKm as number;
      const efficiency = v.fuelEfficiency as number;
      const price = v.fuelPrice as number;

      const litersNeeded = km / efficiency;
      const monthlyCost = Math.round(litersNeeded * price);
      const annualCost = monthlyCost * 12;
      const costPerKm = Math.round((monthlyCost / km) * 100) / 100;

      return { monthlyCost, annualCost, costPerKm, litersNeeded: Math.round(litersNeeded) };
    },
    resultFields: [
      { key: "costPerKm", label: "Cost Per Kilometer", format: "currency", isPrimary: true },
      { key: "monthlyCost", label: "Monthly Fuel Cost", format: "currency" },
      { key: "annualCost", label: "Annual Fuel Cost", format: "currency" },
      { key: "litersNeeded", label: "Liters Per Month", format: "number" },
    ],
    content: {
      whatIs: "Fuel cost per kilometer depends on: fuel efficiency (mileage), fuel price, and driving pattern. Typical car: 12-15 km/liter. Driving 2,000 km/month at ₹110/liter with 15 km/L efficiency: ₹14,667/month, ₹176k/year. Cost per km: ₹7.33. Fuel accounts for 30-40% of vehicle operating cost (rest: maintenance, insurance, depreciation). Improving efficiency by 2-3 km/L saves ₹20-30k yearly.",
      faqs: [
        { question: "What's the average fuel cost per km in India?", answer: "Petrol car (12-15 km/L): ₹6-9/km. Diesel (15-20 km/L): ₹5-7/km. Fuel efficiency varies by car, driving conditions, maintenance." },
        { question: "How can I improve fuel efficiency in India?", answer: "Regular servicing, proper tire pressure, smooth acceleration, highway driving (better than city), reduce load, drive at 60-80 km/h (most efficient speed)." },
        { question: "What's the difference between petrol and diesel fuel cost in India?", answer: "Diesel: ₹5-10/liter cheaper than petrol, 20-30% better efficiency. Diesel cars cost ₹50-200k more upfront. Break-even: 1.5-2 years for high-mileage drivers." },
        { question: "Should I switch to CNG vehicle in India?", answer: "CNG: ₹50-60/liter, cost ₹2-3/km (30-40% cheaper than petrol). Limited model options, slower acceleration, smaller boot. Good for frequent drivers." },
        { question: "How much does EV cost per km in India?", answer: "Electric car: ₹1-2/km (90% cheaper than petrol). Cost depends on electricity rate (₹5-8/unit) and charging efficiency." },
      ],
      relatedSlugs: ["vehicle/ev-vs-petrol-comparison", "vehicle/car-maintenance-cost", "vehicle/toll-calculator"],
    },
  },

  // 9. EV vs Petrol Comparison
  {
    slug: "ev-vs-petrol-comparison",
    category: "vehicle",
    title: "EV vs Petrol Car Total Cost Comparison",
    shortTitle: "EV vs Petrol Cost",
    description: "Compare total cost: EV purchase with FAME II subsidy vs. petrol",
    keywords: ["EV cost", "electric car", "petrol vs electric", "cost comparison"],
    inputs: [
      { type: "number", key: "evPrice", label: "EV Purchase Price", prefix: "₹", min: 1000000, max: 50000000, step: 500000, default: 2500000 },
      { type: "number", key: "petrolPrice", label: "Petrol Car Price", prefix: "₹", min: 500000, max: 10000000, step: 500000, default: 800000 },
      { type: "number", key: "annualKm", label: "Annual Kilometers", suffix: "km", min: 10000, max: 100000, step: 5000, default: 40000 },
      { type: "number", key: "yearsOwned", label: "Ownership Period", suffix: "years", min: 1, max: 20, step: 1, default: 5 },
    ],
    calculate(v) {
      const evPrice = v.evPrice as number;
      const petrolPrice = v.petrolPrice as number;
      const annualKm = v.annualKm as number;
      const years = v.yearsOwned as number;

      // EV costs
      const evDepreciation = evPrice * 0.50; // EVs depreciate ~50% in 5 years
      const evChargingCost = (annualKm / 6) * years * 4; // ₹4/km charging cost
      const evMaintenance = 20000 * years; // ₹20k/year maintenance
      const evInsurance = (evPrice * 0.04) * years; // 4% of price

      const totalEvCost = evDepreciation + evChargingCost + evMaintenance + evInsurance;

      // Petrol costs
      const petrolDepreciation = petrolPrice * (0.40 * years / 5); // 15% per year = 40% in ~5 years
      const petrolFuelCost = (annualKm / 14) * years * 110; // 14 km/L at ₹110/L
      const petrolMaintenance = 50000 * years; // ₹50k/year maintenance
      const petrolInsurance = (petrolPrice * 0.035) * years; // 3.5% of price

      const totalPetrolCost = petrolDepreciation + petrolFuelCost + petrolMaintenance + petrolInsurance;

      const costDifference = Math.round(totalEvCost - totalPetrolCost);
      const cheaper = costDifference < 0 ? "EV" : "Petrol";

      return {
        totalEvCost: Math.round(totalEvCost),
        totalPetrolCost: Math.round(totalPetrolCost),
        costDifference: Math.abs(costDifference),
        cheaper
      };
    },
    resultFields: [
      { key: "cheaper", label: "Cheaper Option", format: "text", isPrimary: true },
      { key: "totalEvCost", label: "EV Total Cost", format: "currency" },
      { key: "totalPetrolCost", label: "Petrol Total Cost", format: "currency" },
      { key: "costDifference", label: "Difference", format: "currency" },
    ],
    content: {
      whatIs: "EV vs petrol comparison: EVs cost 2-3x more upfront but 30-40% cheaper to operate (charging: ₹1-2/km vs fuel: ₹6-8/km). Maintenance: EV ₹20-30k/year, petrol ₹50-80k/year (no oil, spark plugs). Tax: EV registration 50% cheaper in many states. Break-even: 5-7 years for high-mileage drivers (>40k km/year). Long-term (10 years): EV significantly cheaper despite higher purchase price.",
      faqs: [
        { question: "When does EV become cheaper than petrol car in India?", answer: "Break-even: 5-7 years for 40k km/year driving. Earlier if higher mileage or high fuel prices. Battery replacement cost (₹5-8L at 8-10 years) affects long-term economics." },
        { question: "What's the total cost of ownership for EV vs petrol in India?", answer: "5-year EV (₹25L): ₹27-30L total. Petrol (₹8L): ₹10-12L total. EV appears expensive short-term but cheaper in 10+ year scenario." },
        { question: "How much cheaper is EV charging vs petrol in India?", answer: "Charging: ₹1-2/km. Petrol: ₹6-8/km. Annual 40k km: EV ₹40-80k fuel, petrol ₹240-320k fuel. Saving: ₹160-240k yearly on fuel." },
        { question: "What about EV battery replacement cost in India?", answer: "Battery replacement: ₹5-8L at 8-10 years (when degradation >20%). Most EVs: 8-10 year warranty. High cost reduces long-term economics slightly." },
        { question: "Are government incentives available for EV purchase in India?", answer: "FAME subsidy: ₹1.5L for cars (but rarely applicable). State subsidies: ₹1-5L in some states (Delhi, Maharashtra, Karnataka). Check current schemes at your state transport authority." },
      ],
      relatedSlugs: ["vehicle/fuel-cost-per-km", "vehicle/car-loan-emi", "vehicle/car-maintenance-cost"],
    },
  },

  // 10. Car Maintenance Cost
  {
    slug: "car-maintenance-cost",
    category: "vehicle",
    title: "Annual Car Maintenance Cost Estimator",
    shortTitle: "Car Maintenance Cost",
    description: "Calculate annual maintenance and repair expenses for vehicles",
    keywords: ["maintenance cost", "service cost", "repair cost", "vehicle maintenance"],
    inputs: [
      { type: "number", key: "annualKm", label: "Annual Kilometers", suffix: "km", min: 5000, max: 100000, step: 5000, default: 40000 },
      { type: "select", key: "carAge", label: "Car Age", options: [{ value: "new", label: "0-3 years (New)" }, { value: "mid", label: "3-8 years (Mid-life)" }, { value: "old", label: "8+ years (Old)" }], default: "mid" },
      { type: "select", key: "carType", label: "Car Type", options: [{ value: "sedan", label: "Sedan" }, { value: "suv", label: "SUV/MUV" }, { value: "hatchback", label: "Hatchback" }], default: "sedan" },
    ],
    calculate(v) {
      const km = v.annualKm as number;
      const age = v.carAge as string;
      const type = v.carType as string;

      let baseCost = 0;
      if (age === "new") baseCost = 25000;
      else if (age === "mid") baseCost = 50000;
      else baseCost = 80000;

      const kmMultiplier = (km / 40000) * 0.3; // 30% cost varies with km
      const typeMultiplier = type === "sedan" ? 1.0 : type === "suv" ? 1.2 : 0.85;

      const annualCost = Math.round(baseCost * (1 + kmMultiplier) * typeMultiplier);
      const perKmCost = Math.round((annualCost / km) * 100) / 100;

      return { annualCost, perKmCost };
    },
    resultFields: [
      { key: "annualCost", label: "Annual Maintenance Cost", format: "currency", isPrimary: true },
      { key: "perKmCost", label: "Cost Per KM", format: "currency" },
    ],
    content: {
      whatIs: "Annual car maintenance: new car ₹25-40k/year (warranty covers most), mid-life ₹50-80k/year, old car ₹80-150k/year. Includes: scheduled servicing (every 5-10k km), consumables (oil, filters, spark plugs), repairs, wear & tear. SUVs: 15-20% higher; hatchbacks: 10-15% lower. Maintenance cost: 5-10% of car value annually. Delaying maintenance leads to expensive repairs; timely service extends car life by years.",
      faqs: [
        { question: "What's the average annual maintenance cost in India?", answer: "New car: ₹25-40k. Mid-life (3-8yr): ₹50-80k. Old car (8+yr): ₹80-150k. Varies by brand, usage, and maintenance habits." },
        { question: "What expenses are included in car maintenance in India?", answer: "Scheduled servicing, oil/filter changes, spark plugs, air filters, fluid top-ups, brake pads, tire replacements, alignment, battery, electrical repairs." },
        { question: "How often should I service my car in India?", answer: "Every 5,000 km or quarterly, whichever earlier. First service: 1,000 km. Follow manufacturer schedule. Regular service prevents expensive repairs." },
        { question: "Why is maintenance cost higher for old cars in India?", answer: "More repairs needed, parts failure increases, batteries weak, rust, electrical issues, wear on components. Old cars: higher risk of breakdown costs." },
        { question: "Can I reduce car maintenance cost in India?", answer: "Use genuine parts (avoid cheap imitation), regular servicing, follow manufacturer guidelines, drive smoothly (reduces wear), proper tire pressure, good driving habits." },
      ],
      relatedSlugs: ["vehicle/fuel-cost-per-km", "vehicle/car-insurance-calculator", "vehicle/car-depreciation-calculator"],
    },
  },

  // 11. Toll Calculator
  {
    slug: "toll-calculator",
    category: "vehicle",
    title: "Highway Toll Charges Calculator",
    shortTitle: "Toll Calculator",
    description: "Calculate toll charges for highway trips and routes",
    keywords: ["toll calculator", "highway toll", "toll charges", "toll road cost"],
    inputs: [
      { type: "number", key: "distanceKm", label: "Journey Distance", suffix: "km", min: 10, max: 5000, step: 10, default: 200 },
      { type: "select", key: "vehicleType", label: "Vehicle Type", options: [{ value: "car", label: "Car/Auto" }, { value: "suv", label: "SUV/Truck" }, { value: "bike", label: "Bike" }], default: "car" },
      { type: "number", key: "trips", label: "Number of Trips", suffix: "trips", min: 1, max: 52, step: 1, default: 1 },
    ],
    calculate(v) {
      const distance = v.distanceKm as number;
      const vehicle = v.vehicleType as string;
      const trips = v.trips as number;

      // Approximate toll rates: ₹20-25 per 25 km
      let ratePerKm = 0;
      if (vehicle === "car") ratePerKm = 1.0;
      else if (vehicle === "suv") ratePerKm = 1.5;
      else ratePerKm = 0.3; // Bikes cheaper

      const perTripCost = Math.round((distance / 25) * 20 * ratePerKm);
      const totalCost = perTripCost * trips;
      const perKmCost = Math.round((perTripCost / distance) * 100) / 100;

      return { perTripCost, totalCost, perKmCost };
    },
    resultFields: [
      { key: "totalCost", label: "Total Toll Cost", format: "currency", isPrimary: true },
      { key: "perTripCost", label: "Cost Per Trip", format: "currency" },
      { key: "perKmCost", label: "Cost Per KM", format: "currency" },
    ],
    content: {
      whatIs: "Highway toll charges in India: ₹20-25 per 25 km for cars (varies by highway and state). SUVs/trucks: 50-100% higher. Bikes: 30-50% of car rate. Fast tag (FASTag) provides 4% discount and faster passage. Monthly passes available for frequent travelers (₹2-5k for unlimited passes on specific highways). Toll typically adds ₹1-2/km to journey cost, significant for long-distance travel.",
      faqs: [
        { question: "What's the toll rate on Indian highways?", answer: "Approximately ₹20-25 per 25 km for cars. ₹400-500 for 500 km journey. SUVs: 1.5-2x cars. Rates vary by highway operator and state." },
        { question: "Can I avoid toll charges on highways in India?", answer: "No, toll mandatory on national highways. FASTag: 4% discount but still payable. Alternate routes: may avoid toll but longer distance and fuel cost." },
        { question: "What's FASTag and is it worth it in India?", answer: "Electronic toll collection: 4% discount, faster passage (no manual collection). Mandatory since 2021. Free reader installation. Useful for frequent highway travel." },
        { question: "How much toll charge for 500 km highway journey in India?", answer: "Car: ₹400-500. SUV: ₹600-750. Includes both directions if round trip. Monthly pass cheaper if traveling frequently." },
        { question: "Are there monthly or yearly toll passes in India?", answer: "Yes, specific highways offer monthly/quarterly passes. Cost: ₹2-5k/month for unlimited passes. Worth it if traveling 2+ times/week." },
      ],
      relatedSlugs: ["vehicle/fuel-cost-per-km", "vehicle/cab-vs-car-cost", "personal-finance/travel-insurance-cost"],
    },
  },

  // 12. Cab vs Car Cost
  {
    slug: "cab-vs-car-cost",
    category: "vehicle",
    title: "Monthly Cab vs Owning Car Cost Comparison",
    shortTitle: "Cab vs Car",
    description: "Compare total cost: owning a car vs. using ride-sharing services",
    keywords: ["cab cost", "taxi cost", "car ownership", "cost comparison"],
    inputs: [
      { type: "number", key: "monthlyKm", label: "Monthly Kilometers", suffix: "km", min: 1000, max: 10000, step: 500, default: 2000 },
      { type: "number", key: "cabRatePerKm", label: "Cab Rate Per KM", prefix: "₹", min: 10, max: 50, step: 2, default: 25 },
      { type: "number", key: "carEmi", label: "Monthly Car EMI (if financed)", prefix: "₹", min: 0, max: 100000, step: 5000, default: 20000 },
      { type: "number", key: "carOtherCosts", label: "Monthly Car Costs (Fuel+Insurance+Maintenance)", prefix: "₹", min: 5000, max: 50000, step: 5000, default: 15000 },
    ],
    calculate(v) {
      const km = v.monthlyKm as number;
      const cabRate = v.cabRatePerKm as number;
      const emi = v.carEmi as number;
      const other = v.carOtherCosts as number;

      const cabCost = km * cabRate;
      const carCost = emi + other;
      const difference = Math.abs(cabCost - carCost);
      const cheaper = cabCost < carCost ? "Cab" : "Car";

      return { cabCost, carCost, difference, cheaper };
    },
    resultFields: [
      { key: "cheaper", label: "Cheaper Option", format: "text", isPrimary: true },
      { key: "cabCost", label: "Monthly Cab Cost", format: "currency" },
      { key: "carCost", label: "Monthly Car Cost", format: "currency" },
      { key: "difference", label: "Monthly Difference", format: "currency" },
    ],
    content: {
      whatIs: "Cab (Ola/Uber) vs car ownership cost: cab ₹20-35/km, car ₹30-50/km including EMI+fuel+insurance+maintenance. For <2,000 km/month: cab cheaper. For >2,000 km/month: car cheaper (fixed costs amortized). Cab advantages: flexibility, no parking hassle, maintenance-free. Car advantages: privacy, reliability, asset ownership. Urban professionals (<1,500 km/month) prefer cab; families (>3,000 km/month) prefer car ownership.",
      faqs: [
        { question: "When is cab cheaper than car ownership in India?", answer: "Cab cheaper if <1,500 km/month. Break-even around 2,000 km/month. >2,500 km: car usually cheaper due to fixed cost amortization." },
        { question: "What's the average cab rate in Indian cities?", answer: "Ola/Uber: ₹25-35/km in metros (Delhi, Mumbai, Bangalore). Off-peak: ₹20-25/km. Peak hours: ₹40-50/km. Auto: ₹8-15/km." },
        { question: "What expenses should I include in car ownership cost?", answer: "EMI (if financed), fuel (₹7-10/km), insurance (₹1-2k/month), maintenance (₹3-5k/month), parking (₹500-2k/month), registration renewal (₹100-200/month average)." },
        { question: "Is car ownership better for environment vs cab in India?", answer: "Per person, car ownership worse (1-2 occupants). Cabs better (3-4 occupants), but electric cabs best. Carpooling most efficient." },
        { question: "Should I buy car outright or finance in India for cost comparison?", answer: "Financing spreads cost over time. Down payment + EMI vs full payment. EMI option better for cash flow, but you pay interest. Total cost higher with financing." },
      ],
      relatedSlugs: ["vehicle/fuel-cost-per-km", "vehicle/car-loan-emi", "vehicle/car-maintenance-cost"],
    },
  },

  // 13. Vehicle Scrappage Value
  {
    slug: "vehicle-scrappage-value",
    category: "vehicle",
    title: "Vehicle Scrappage Value Calculator",
    shortTitle: "Scrappage Value",
    description: "Calculate scrappage value under vehicle scrappage policy",
    keywords: ["scrappage", "vehicle scrappage", "scrap value", "old vehicle value"],
    inputs: [
      { type: "number", key: "vehiclePrice", label: "Original Purchase Price", prefix: "₹", min: 300000, max: 50000000, step: 100000, default: 1000000 },
      { type: "number", key: "ageYears", label: "Vehicle Age", suffix: "years", min: 10, max: 50, step: 1, default: 15 },
      { type: "select", key: "vehicleType", label: "Vehicle Type", options: [{ value: "car", label: "Car" }, { value: "suv", label: "SUV" }, { value: "truck", label: "Truck" }], default: "car" },
    ],
    calculate(v) {
      const price = v.vehiclePrice as number;
      const age = v.ageYears as number;
      const type = v.vehicleType as string;

      // Scrappage: 4-6% of original price for vehicles >10 years
      let scrapPercent = 0;
      if (age >= 20) scrapPercent = 0.04;
      else if (age >= 15) scrapPercent = 0.05;
      else scrapPercent = 0.06;

      const typeMultiplier = type === "car" ? 1.0 : type === "suv" ? 1.3 : 1.5;
      const scrapValue = Math.round(price * scrapPercent * typeMultiplier);

      // Government incentive: additional benefit if scrapping
      const govIncentive = scrapValue * 0.25; // 25% additional incentive

      return { scrapValue, govIncentive: Math.round(govIncentive), totalValue: Math.round(scrapValue + govIncentive) };
    },
    resultFields: [
      { key: "scrapValue", label: "Scrappage Value", format: "currency", isPrimary: true },
      { key: "govIncentive", label: "Government Incentive (25%)", format: "currency" },
      { key: "totalValue", label: "Total Scrappage Benefit", format: "currency" },
    ],
    content: {
      whatIs: "Vehicle scrappage policy in India: owners of vehicles >15 years old can scrap with benefit. Scrappage value: 4-6% of original price. Government incentive: 25% extra benefit. Total: 5-8% of original price. For ₹10L car (20+ years): scrappage ₹40-50k + incentive ₹10-12k = ₹50-62k benefit. Plus: tax waiver, discount on new vehicle purchase (₹1-1.5L), disposal certificate. Aimed at reducing pollution from old vehicles.",
      faqs: [
        { question: "What's India's vehicle scrappage policy details?", answer: "Vehicles >15 years eligible for scrappage. Owners get 4-6% of cost + 25% incentive + tax benefits. New vehicle purchase: ₹1-1.5L discount. RTO deregistration required." },
        { question: "How much do I get for scrapping old vehicle in India?", answer: "Scrappage value: 4-6% of original price. ₹10L car (20yr): ₹40-60k. Gov incentive: 25% extra. Plus discount on new purchase (₹1-1.5L)." },
        { question: "Is scrappage better than selling old car in India?", answer: "Scrappage value (4-6%) very low. Private sale (10-20%) much better even for very old cars. Use scrappage if car unsaleable due to condition." },
        { question: "What's required to scrap a vehicle in India?", answer: "Contact authorized scrappage center (ASDC), provide RC, insurance, pollution certificate. CNG/electric vehicles eligible too (same benefits)." },
        { question: "What discount available on new car purchase with scrappage in India?", answer: "Direct discount from government: ₹1-1.5L. Some dealerships: additional discount (₹50k-1L) for old trade-in. Total: ₹1.5-2.5L potential savings." },
      ],
      relatedSlugs: ["vehicle/car-resale-value", "vehicle/car-depreciation-calculator", "vehicle/vehicle-loan-eligibility"],
    },
  },

  // 14. Car Lease vs Buy
  {
    slug: "car-lease-vs-buy",
    category: "vehicle",
    title: "Car Lease vs Buy Analysis",
    shortTitle: "Lease vs Buy",
    description: "Compare total cost: leasing a car vs. outright purchase",
    keywords: ["lease", "car lease", "car rental", "lease vs buy"],
    inputs: [
      { type: "number", key: "carPrice", label: "Car Purchase Price", prefix: "₹", min: 500000, max: 20000000, step: 500000, default: 1000000 },
      { type: "number", key: "monthlyLease", label: "Monthly Lease Cost", prefix: "₹", min: 10000, max: 100000, step: 5000, default: 20000 },
      { type: "number", key: "yearsOwned", label: "Ownership/Lease Duration", suffix: "years", min: 1, max: 10, step: 1, default: 5 },
      { type: "number", key: "monthlyOwnership", label: "Monthly Ownership Cost (EMI+others)", prefix: "₹", min: 5000, max: 100000, step: 5000, default: 25000 },
    ],
    calculate(v) {
      const price = v.carPrice as number;
      const lease = v.monthlyLease as number;
      const years = v.yearsOwned as number;
      const ownership = v.monthlyOwnership as number;

      const leaseCost = lease * 12 * years;
      const ownershipCost = ownership * 12 * years;

      // Residual value at end (30-50% for 5-year old)
      const residualValue = Math.round(price * (0.3 + (5 - years) * 0.04));
      const netBuyCost = ownershipCost - residualValue;

      const difference = Math.abs(leaseCost - netBuyCost);
      const cheaper = leaseCost < netBuyCost ? "Lease" : "Buy";

      return { leaseCost, netBuyCost, residualValue, difference, cheaper };
    },
    resultFields: [
      { key: "cheaper", label: "Cheaper Option", format: "text", isPrimary: true },
      { key: "leaseCost", label: "Total Lease Cost", format: "currency" },
      { key: "netBuyCost", label: "Net Buy Cost (After Resale)", format: "currency" },
      { key: "residualValue", label: "Car Resale Value", format: "currency" },
    ],
    content: {
      whatIs: "Lease vs buy: leasing (₹15-30k/month) suits short-term (2-3 years), minimal usage (under 40k km/year), preference for new cars. Buying suits long-term (5+ years), high usage (40k+ km/year), customization needs. Lease: no maintenance, insurance included, easy upgrade. Buy: ownership, unlimited mileage, customization, lower total cost long-term. Lease good for frequent business travelers; buy good for families.",
      faqs: [
        { question: "What's the difference between lease and car ownership in India?", answer: "Lease: monthly fee, no ownership, mileage limit (40k-60k km/year), includes maintenance/insurance. Buy: own car, unlimited mileage, maintenance cost, insurance separate." },
        { question: "Is leasing cheaper than buying in India?", answer: "Short-term (2-3yr): lease cheaper. Long-term (5-7yr): buy cheaper. Break-even around 4-5 years. Usage >40k km/year: buy better." },
        { question: "What are lease mileage limits in India?", answer: "Typical: 40k-60k km/year. Exceeding costs ₹1-3/km extra. Total mileage over lease period: usually 200k-300k km maximum." },
        { question: "What's included in lease cost in India?", answer: "Vehicle, insurance, regular maintenance, roadside assistance. Not included: fuel, toll, parking, extra mileage charges, damage beyond normal wear." },
        { question: "Can I lease a car without down payment in India?", answer: "Most lease require: first month + security deposit (₹50-100k). Some no-down-payment offers available. Read fine print for hidden charges." },
      ],
      relatedSlugs: ["vehicle/car-loan-emi", "vehicle/car-maintenance-cost", "vehicle/cab-vs-car-cost"],
    },
  },

  // 15. Vehicle Loan Eligibility
  {
    slug: "vehicle-loan-eligibility",
    category: "vehicle",
    title: "Vehicle Loan Eligibility Calculator",
    shortTitle: "Loan Eligibility",
    description: "Check loan eligibility and maximum loan amount for vehicles",
    keywords: ["loan eligibility", "loan amount", "affordability", "lending criteria"],
    inputs: [
      { type: "number", key: "monthlyIncome", label: "Monthly Income", prefix: "₹", min: 25000, max: 10000000, step: 25000, default: 100000 },
      { type: "number", key: "existingEmi", label: "Existing Monthly EMI", prefix: "₹", min: 0, max: 500000, step: 5000, default: 0 },
      { type: "number", key: "creditScore", label: "Credit Score (CIBIL)", suffix: "score", min: 300, max: 900, step: 50, default: 750 },
      { type: "number", key: "downPaymentPercent", label: "Down Payment Capacity", suffix: "%", min: 10, max: 50, step: 5, default: 25 },
    ],
    calculate(v) {
      const income = v.monthlyIncome as number;
      const emi = v.existingEmi as number;
      const score = v.creditScore as number;
      const downPercent = (v.downPaymentPercent as number) / 100;

      // Bank lending criteria: max EMI = 50% of income
      const availableEmi = income * 0.50;
      const maxNewEmi = availableEmi - emi;

      if (maxNewEmi <= 0) {
        return { maxLoanEligible: 0, maxCarPrice: 0, recommendation: "Too much existing debt" as any };
      }

      // Convert EMI to loan amount (assuming 9% interest, 5-year tenure)
      const monthlyRate = 0.09 / 12;
      const months = 60;
      const loanMultiplier = (Math.pow(1 + monthlyRate, months) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, months));
      const maxLoan = Math.round(maxNewEmi * loanMultiplier);

      // Max car price based on 75% LTV
      const maxCarPrice = Math.round(maxLoan / 0.75);

      let recommendation = "";
      if (score < 650) recommendation = "Low CIBIL: limited lenders";
      else if (score < 750) recommendation = "Average CIBIL: higher rates";
      else recommendation = "Good CIBIL: best rates available";

      return { maxLoanEligible: maxLoan, maxCarPrice, downPaymentRequired: Math.round(maxCarPrice * downPercent), recommendation };
    },
    resultFields: [
      { key: "maxCarPrice", label: "Max Car Price Eligible", format: "currency", isPrimary: true },
      { key: "maxLoanEligible", label: "Max Loan Amount", format: "currency" },
      { key: "downPaymentRequired", label: "Recommended Down Payment", format: "currency" },
      { key: "recommendation", label: "CIBIL Assessment", format: "text" },
    ],
    content: {
      whatIs: "Vehicle loan eligibility depends: monthly income (banks allow max 50% for EMI), existing debt, CIBIL score (750+ best rates), age (25-60 years), employment type (salaried preferred over self-employed), and down payment capacity. Max loan: 75-90% of vehicle price. For ₹10L income: max EMI ₹50k, loan ₹15-20L, car price ₹20-27L (with 25-30% down payment). Lower income, higher existing EMI: loan amount reduced.",
      faqs: [
        { question: "How much car loan can I get based on my income in India?", answer: "Max EMI = 50% of income. For ₹10L income: max ₹50k EMI = ₹15-20L loan (at 9%, 5 years). Car price: ₹20-27L with 25-30% down." },
        { question: "Does CIBIL score affect vehicle loan eligibility in India?", answer: "Yes, significantly. <650: rejection/limited lenders. 650-750: approval but higher rates (10.5-11%). 750+: best rates (8-9%). Check CIBIL before applying." },
        { question: "What's the maximum LTV (Loan-to-Value) for car loans in India?", answer: "Typically 75-90%. For ₹10L car: borrow ₹7.5-9L. Down payment: 10-25%. Luxury cars: lower LTV (60-75%)." },
        { question: "Can self-employed person get car loan in India?", answer: "Yes, but stricter criteria: 2-3 years ITR required, higher interest rate (+0.5-1%), more documentation, sometimes lower LTV (60-75%)." },
        { question: "How does existing debt affect car loan eligibility in India?", answer: "Debt-to-income ratio: max 50% of income for all EMI. With ₹20k existing EMI: max ₹50k total = ₹30k new car EMI only. Higher existing debt reduces eligibility." },
      ],
      relatedSlugs: ["vehicle/car-loan-emi", "vehicle/two-wheeler-loan-emi", "personal-finance/emi-vs-savings"],
    },
  },
];
